import { useState, useEffect, useRef } from 'react'
import { Search, MapPin, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  searchPlaces,
  getPlaceDetails,
  parsePlaceToOdinFormat,
  formatSuggestionForDisplay,
  validateApiKey
} from '../services/googlePlaces'

/**
 * AddressFinder Component
 *
 * Provides Google Places autocomplete for address fields.
 * Displays a "Find Address" button that transforms into a searchable dropdown.
 *
 * @param {string} businessName - Initial search query (usually company/branch name)
 * @param {function} onAddressSelect - Callback with parsed address data
 * @param {boolean} disabled - Disable the component
 * @param {string} variant - Visual variant: 'company' or 'branch'
 */
export default function AddressFinder({
  businessName = '',
  onAddressSelect,
  disabled = false,
  variant = 'company'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const searchTimeoutRef = useRef(null)

  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

  // Initialize search query with business name when popover opens
  useEffect(() => {
    if (isOpen && businessName && !searchQuery) {
      setSearchQuery(businessName)
    }
  }, [isOpen, businessName])

  // Debounced search when query changes
  useEffect(() => {
    if (!isOpen || !searchQuery.trim()) {
      setSuggestions([])
      return
    }

    // Validate API key
    if (!validateApiKey(apiKey)) {
      setError('Google Places API key is not configured')
      return
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true)
      setError(null)

      try {
        const results = await searchPlaces(searchQuery, apiKey)
        setSuggestions(results)
      } catch (err) {
        console.error('[AddressFinder] Search failed:', err)
        setError(err.message || 'Failed to search addresses')
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 300) // 300ms debounce

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, isOpen, apiKey])

  const handleSuggestionSelect = async (placeId) => {
    if (!placeId) return

    setLoading(true)
    setError(null)

    try {
      // Fetch full place details
      const placeDetails = await getPlaceDetails(placeId, apiKey)

      // Parse into Odin format
      const addressData = parsePlaceToOdinFormat(placeDetails)

      // Call parent callback with address data
      onAddressSelect(addressData)

      // Close popover and reset state
      setIsOpen(false)
      setSearchQuery('')
      setSuggestions([])
    } catch (err) {
      console.error('[AddressFinder] Failed to get place details:', err)
      setError(err.message || 'Failed to fetch address details')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setIsOpen(false)
    setSearchQuery('')
    setSuggestions([])
    setError(null)
  }

  const handleOpenChange = (open) => {
    setIsOpen(open)
    if (!open) {
      // Reset state when closing
      setSearchQuery('')
      setSuggestions([])
      setError(null)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            "gap-2 text-sm font-medium transition-colors",
            variant === 'company' && "text-gray-700 hover:text-gray-900",
            variant === 'branch' && "text-gray-600 hover:text-gray-800"
          )}
        >
          <Search className="h-4 w-4" />
          Find Address
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[400px] p-0"
        align="end"
        side="bottom"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search for address..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Cancel</span>
            </Button>
          </div>

          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching addresses...
              </div>
            )}

            {error && (
              <div className="py-6 px-4 text-center">
                <p className="text-sm text-red-600">{error}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setError(null)}
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            )}

            {!loading && !error && suggestions.length === 0 && searchQuery.trim() && (
              <CommandEmpty>
                <div className="py-6 text-center">
                  <MapPin className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">No addresses found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                </div>
              </CommandEmpty>
            )}

            {!loading && !error && suggestions.length > 0 && (
              <CommandGroup>
                {suggestions.map((suggestion) => {
                  const formatted = formatSuggestionForDisplay(suggestion)
                  return (
                    <CommandItem
                      key={formatted.id}
                      value={formatted.id}
                      onSelect={() => handleSuggestionSelect(formatted.id)}
                      className="flex items-start gap-2 py-3 px-3 cursor-pointer hover:bg-gray-50"
                    >
                      <MapPin className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {formatted.primaryText}
                        </div>
                        {formatted.secondaryText && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            {formatted.secondaryText}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>

        <div className="border-t px-3 py-2 text-xs text-gray-500 flex items-center justify-between">
          <span>Powered by Google</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-7 text-xs"
          >
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
